import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FlowComponentType } from 'routes/types';
import Button from 'components/Button/Button';
import FormContainer from 'components/LoanForm/FormContainer/FormContainer';
import RangeSlider from 'components/RangeSlider';
import OfferCard from 'components/OfferCard';
import PaymentMethodSwitch from 'components/PaymentMethodSwitch';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { goalIsPersonalFundsOnly } from 'handlers/yourGoal';
import { SaveOfferData } from 'api/ReportsApi';
import { getReportsState } from 'selectors/getOffers';
import { getOffers, saveSelectedOffer, updateApplicationCurrentFlow } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import StateContainer from 'components/StateContainer';
import { ReactComponent as LoadingSpinner } from 'images/loader.svg';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { hasPayDownCreditCardDebtGoal } from 'utils/aiCoachUtils';
import { PayFrequencyOptionValue } from 'handlers/yourTotalIncome';
import BackLink from 'components/BackLink';
import { RoutePath } from 'enums/Routes';
import { CurrentFlow } from 'enums/CurrentFlow';

import {
  getSliderStep,
  getOffersForAmount,
  sortOffersByGoals,
  getOfferBadgeType,
  getHeaderInfo,
  getSliderMax,
  isValidOfferAmount,
} from './offerUtils';

import styles from './OfferAvailable.module.scss';

const OfferAvailable = ({ handleNext, fromDashboard }: FlowComponentType & { fromDashboard?: boolean }) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoading: isApplicationLoading, application } = useSelector(getApplicationData);
  const { aiCoachGoals } = useSelector(getLoanGoal);
  const {
    id: applicationId,
    loanSelectedTerm,
    loanConsolidationAmount = 0,
    loanTermInMonths,
    interestRate,
    loanGoal = [],
    promo,
  } = application ?? {};
  const { isLoading: isLoadingOffers, offers: offersData } = useSelector(getReportsState);
  const isLoading = isLoadingOffers || isApplicationLoading || isSubmitting;
  const trimmedOffersData = offersData.slice(-3);

  const { maxLoanAmount = 0, minLoanAmount = 0 } = application ?? {};

  const sliderMax = getSliderMax(trimmedOffersData);

  const eligibleForSingleAmountLoan = minLoanAmount >= sliderMax;

  const additionalFundsOnly = goalIsPersonalFundsOnly(loanGoal);

  const [offerAmount, setOfferAmount] = useState(
    loanConsolidationAmount < sliderMax && isValidOfferAmount(loanConsolidationAmount, trimmedOffersData)
      ? loanConsolidationAmount
      : sliderMax,
  );
  const [selectedOfferIndex, setSelectedOfferIndex] = useState<number | undefined>(undefined);
  const [existingOfferSelected, setExistingOfferSelected] = useState<boolean>(false);

  const availableOffersForAmount = sortOffersByGoals(getOffersForAmount(trimmedOffersData, offerAmount), loanGoal);

  const offerSelected = availableOffersForAmount[selectedOfferIndex ?? 0];
  const offerHasInterestAndTimeSavings = offerSelected?.totalInterestSavings && offerSelected?.totalMonthsSaved;

  const sliderStep = getSliderStep(trimmedOffersData);

  useEffect(() => {
    // Once the application is updated, navigate to the next step
    if (isSubmitting) handleNextNavigation();
  }, [application]);

  useEffect(() => {
    const updateCurrentFlow = async () => {
      const shouldSwitchToDebtConsolidationFlow =
        applicationId && application?.currentFlow === CurrentFlow.AICoach && hasPayDownCreditCardDebtGoal(aiCoachGoals);

      if (shouldSwitchToDebtConsolidationFlow) {
        await dispatchWithUnwrap(
          updateApplicationCurrentFlow({
            applicationId: applicationId!,
            currentFlow: CurrentFlow.DebtConsolidation,
          }),
        );
      }
    };
    updateCurrentFlow();
  }, [applicationId, application?.currentFlow, aiCoachGoals]);

  useEffect(() => {
    // Automatically select the offer if the user has already selected one
    if (availableOffersForAmount.length > 0 && selectedOfferIndex === undefined) {
      if (loanSelectedTerm !== undefined) {
        const currentOfferIndex = availableOffersForAmount.findIndex(
          (offer) => offer.totalPayments === loanSelectedTerm,
        );

        if (currentOfferIndex !== -1) {
          setSelectedOfferIndex(currentOfferIndex);
          setExistingOfferSelected(true);
        } else {
          setSelectedOfferIndex(0);
        }
      } else {
        setSelectedOfferIndex(0);
      }
    }
  }, [availableOffersForAmount]);

  const handleNextNavigation = () => {
    setIsSubmitting(false);
    handleNext();
  };

  const handleContinue = async () => {
    setIsSubmitting(true);

    if (applicationId && offerSelected && trimmedOffersData.length > 0) {
      if (existingOfferSelected) {
        const interestRateFormatted = Number(interestRate?.toFixed(2));
        const sameOfferSelected =
          loanTermInMonths === offerSelected.totalPayments &&
          interestRateFormatted === offerSelected.totalInterestRate &&
          loanConsolidationAmount === offerAmount;

        if (sameOfferSelected) return handleNextNavigation();
      }

      const selectedOffer = trimmedOffersData.find((offer) => offer.term === offerSelected.totalPayments);

      // Save the selected offer before navigating to the next step
      if (selectedOffer) {
        const calculationForSelectedAmount = selectedOffer.calculations.find((calc) => calc.loanAmount === offerAmount);

        if (calculationForSelectedAmount) {
          const saveData: SaveOfferData = {
            offerData: {
              tier: selectedOffer.tier,
              term: selectedOffer.term,
              loanAmount: calculationForSelectedAmount.loanAmount,
            },
          };

          await dispatchWithUnwrap(
            saveSelectedOffer({
              applicationId,
              data: saveData,
            }),
          );
        }
      }
    }
  };

  const headerInfo = getHeaderInfo(additionalFundsOnly, eligibleForSingleAmountLoan, maxLoanAmount, sliderMax);

  return (
    <>
      {fromDashboard && <BackLink onClick={() => navigate(RoutePath.FinancialDashboard)} />}
      <FormContainer
        title={headerInfo.title}
        subtitle={headerInfo.showSubtitle ? "Later, you'll pick the accounts to pay off." : undefined}
      >
        <div className={styles.mainContainer}>
          {!eligibleForSingleAmountLoan && (
            <RangeSlider
              min={minLoanAmount}
              max={sliderMax}
              step={sliderStep}
              isLoading={isLoading}
              disabled={isLoading}
              value={offerAmount}
              onChange={(value) => setOfferAmount(value)}
            />
          )}
          <div className={styles.choosePlanContainer}>
            <div className={styles.choosePlanHeader}>
              <h3>{availableOffersForAmount.length > 1 ? 'Choose a plan' : 'Your plan'}</h3>

              {application?.canSwitchPaymentMethods && <PaymentMethodSwitch disabled={isLoading} />}

              {promo && (
                // eslint-disable-next-line react/no-danger
                <div className={styles.promoContainer} dangerouslySetInnerHTML={{ __html: promo.bannerContent }} />
              )}
              {(offerSelected?.totalInterestSavings || offerSelected?.totalMonthsSaved) && (
                <div className={styles.savingsContainer}>
                  <p>
                    Save up to{' '}
                    {offerSelected.totalInterestSavings && (
                      <span>
                        {formatMonetaryAmount(offerSelected.totalInterestSavings)} in interest
                        {!offerHasInterestAndTimeSavings && '.'}
                      </span>
                    )}
                  </p>
                  <p>
                    {offerHasInterestAndTimeSavings && ' and '}
                    {offerSelected.totalMonthsSaved && <span>{offerSelected.totalMonthsSaved} months.</span>}*
                  </p>
                </div>
              )}
            </div>

            <div className={styles.gapContainer}>
              {availableOffersForAmount.map((offer, index) => (
                <OfferCard
                  key={`${offer.paymentPerPaycheck}-${offer.totalPayments}`}
                  isSelected={index === selectedOfferIndex}
                  onSelect={() => {
                    if (isLoading) return;
                    setSelectedOfferIndex(index);
                  }}
                  paymentPerPaycheck={offer.paymentPerPaycheck}
                  totalInterestRate={offer.totalInterestRate}
                  baseInterestRate={offer.baseInterestRate}
                  apr={offer.apr}
                  totalPayments={offer.totalPayments}
                  paymentMethod={application?.paymentMethod}
                  isLoading={isLoading}
                  badgeType={getOfferBadgeType(offer, availableOffersForAmount)}
                  paymentFrequency={application?.employment?.employer1?.payFrequency as PayFrequencyOptionValue}
                  achPaymentAmount={offer.achPaymentAmount}
                />
              ))}
            </div>
          </div>
          <Button onClick={handleContinue} disabled={isLoading} isLoading={isSubmitting}>
            Next
          </Button>
          <div className={styles.disclaimerContainer}>
            {!additionalFundsOnly && (
              <p>Your plan may change based on the debt you choose to pay off and if you request additional funds.</p>
            )}
          </div>
        </div>
      </FormContainer>
    </>
  );
};

export interface OfferAvailableState {
  updateOffers?: boolean;
  fromDashboard?: boolean;
}

const OfferAvailableLoadingWrapper = (args: FlowComponentType) => {
  const dispatch = useDispatch();
  const { isLoading: isLoadingOffers, offers: offersData } = useSelector(getReportsState);
  const { id: applicationId } = useSelector(getApplicationData)?.application ?? {};
  const offerAvailableState = useLocation().state as OfferAvailableState;
  const updateOffers = offerAvailableState?.updateOffers;
  const fromDashboard = offerAvailableState?.fromDashboard;

  useEffect(() => {
    if (applicationId && (offersData.length === 0 || updateOffers)) dispatch(getOffers(applicationId));
  }, [applicationId, updateOffers]);

  if (isLoadingOffers && offersData.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <StateContainer icon={<LoadingSpinner />} title="Loading..." />
      </div>
    );
  }

  return <OfferAvailable {...args} fromDashboard={fromDashboard} />;
};

export default OfferAvailableLoadingWrapper;
